import React, { useCallback, useMemo, useState } from 'react';

import { Box, Stack, Text, TextInput, useMantineTheme } from '@mantine/core';
import { IconSearch } from '@tabler/icons-react';
import Table from 'components/Table';
import dayjs from 'dayjs';
import useAssignWorkout from 'hooks/workout/useAssignWorkout';
import useWorkout from 'hooks/workout/useWorkout';
import useWorkouts from 'hooks/workout/useWorkouts';
import AssignedToCell from 'pages/private/owner/Workouts/components/AssignedToCell';
import { useNavigate } from 'react-router-dom';
import { CellProps, Column } from 'react-table';
import type { Workout, WorkoutList } from 'types';
import notificationHandler from 'utils/notifications';
import type { AssignmentTarget } from 'utils/workout.helpers';

import WorkoutPreviewModal from '../../WorkoutPreviewModal';
import useAssignTracking from '../useAssignTracking';
import {
  buildPreviewDataFromWorkout,
  buildWorkoutWriteFromWorkout,
  mapWorkoutToFormValues,
} from '../utils/templateToWorkoutWrite';

interface PanelWorkoutProps {
  target: AssignmentTarget;
  date: string;
  onWorkoutAssigned?: (workout: Workout) => void;
  onClose: () => void;
  view?: 'week' | 'agenda';
  replacesWorkoutId?: string;
  defaultVisibleToAthletes: boolean;
}

const TABLE_HEIGHT = '25rem';

const PanelWorkout = ({
  target,
  date,
  onWorkoutAssigned,
  onClose,
  view,
  replacesWorkoutId,
  defaultVisibleToAthletes,
}: PanelWorkoutProps): JSX.Element => {
  const theme = useMantineTheme();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [selectedWorkout, setSelectedWorkout] = useState<WorkoutList | null>(null);
  const [previewOpened, setPreviewOpened] = useState(false);

  const { successNotification, errorNotification } = notificationHandler();
  const assignMutation = useAssignWorkout(replacesWorkoutId);
  const { trackConfirmed } = useAssignTracking(target);

  const {
    data: workouts,
    isLoading,
    isSuccess,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useWorkouts({
    search: search || undefined,
    ordering: '-date',
    limit: '20',
  });

  const { data: workoutResponse, isLoading: workoutLoading } = useWorkout(
    previewOpened && selectedWorkout ? selectedWorkout.workoutId : undefined,
  );
  const workoutData = workoutResponse?.data;

  const tableData = useMemo(() => {
    if (!isSuccess || !workouts?.pages) return [];
    return workouts.pages.flatMap(page => page.data.results);
  }, [isSuccess, workouts]);

  const columns: Column<WorkoutList>[] = useMemo(
    () => [
      {
        Header: 'Workout Name',
        accessor: 'name' as const,
        Cell: ({ row }: CellProps<WorkoutList>) => <Text weight='bold'>{row.original.name}</Text>,
      },
      {
        Header: 'Assigned To',
        accessor: 'kiloClassType' as const,
        Cell: ({ row }: CellProps<WorkoutList>) => <AssignedToCell workout={row.original} />,
      },
      {
        Header: 'Date',
        accessor: 'date' as const,
        Cell: ({ row }: CellProps<WorkoutList>) => (
          <Text>{dayjs(row.original.date).format('MMM D, YYYY')}</Text>
        ),
      },
    ],
    [],
  );

  const handleRowClick = useCallback((row: WorkoutList) => {
    setSelectedWorkout(row);
    setPreviewOpened(true);
  }, []);

  const handleClosePreview = useCallback(() => {
    setPreviewOpened(false);
    setSelectedWorkout(null);
  }, []);

  const handleAssign = useCallback(
    (isVisibleToAthletes: boolean): void => {
      if (!selectedWorkout || !workoutData) return;
      const payload = buildWorkoutWriteFromWorkout(workoutData, date, target, isVisibleToAthletes);
      assignMutation.mutate(payload, {
        onSuccess: res => {
          trackConfirmed(payload);
          successNotification(
            'Workout assigned',
            `"${selectedWorkout.name}" has been assigned to ${target.name}.`,
          );
          handleClosePreview();
          onWorkoutAssigned?.(res.data);
          onClose();
        },
        onError: error => {
          const message = error.response?.data?.message ?? 'Could not assign the workout. Please try again.';
          errorNotification('Assignment failed', message);
        },
      });
    },
    [
      selectedWorkout,
      workoutData,
      date,
      target,
      assignMutation,
      trackConfirmed,
      successNotification,
      errorNotification,
      handleClosePreview,
      onClose,
      onWorkoutAssigned,
    ],
  );

  const handleEditBeforeAssign = useCallback((): void => {
    if (!selectedWorkout || !workoutData) return;
    navigate('/workouts/programming/create', {
      state: {
        date,
        target,
        prefillData: {
          ...mapWorkoutToFormValues(workoutData),
          isVisibleToAthletes: defaultVisibleToAthletes,
        },
        replacesWorkoutId,
        view,
      },
    });
    handleClosePreview();
    onClose();
  }, [
    selectedWorkout,
    workoutData,
    navigate,
    date,
    target,
    view,
    handleClosePreview,
    onClose,
    defaultVisibleToAthletes,
    replacesWorkoutId,
  ]);

  const rowClassName = useCallback(
    (row: WorkoutList) => {
      return selectedWorkout?.workoutId === row.workoutId ? 'assign-modal-row-selected' : '';
    },
    [selectedWorkout],
  );

  const previewData = selectedWorkout && workoutData ? buildPreviewDataFromWorkout(workoutData) : null;

  return (
    <>
      <Stack spacing='md'>
        <TextInput
          placeholder='Search workouts...'
          value={search}
          onChange={e => setSearch(e.currentTarget.value)}
          icon={<IconSearch size={theme.fontSizes.sm} />}
        />

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: TABLE_HEIGHT,
            '& table tbody td': {
              paddingTop: `${theme.spacing.sm} !important`,
              paddingBottom: `${theme.spacing.sm} !important`,
            },
            '& table tbody tr': {
              cursor: 'pointer',
              transition: 'background-color 120ms ease',
              '&:hover': {
                backgroundColor: theme.colors.gray[0],
              },
            },
            '& .assign-modal-row-selected': {
              backgroundColor: theme.colors.blue[0],
              '&:hover': {
                backgroundColor: theme.colors.blue[1],
              },
            },
          }}
        >
          <Table
            fillHeight
            tableData={tableData}
            columns={columns}
            isSuccess={isSuccess}
            isLoading={isLoading}
            isError={isError}
            isFetchingNextPage={isFetchingNextPage}
            hasNextPage={!!hasNextPage}
            fetchNextPage={fetchNextPage ?? (() => {})}
            margin={0}
            bgc='white'
            handleRowClick={handleRowClick}
            rowClassName={rowClassName}
            EmptyState={
              <Text size='sm' color='dimmed' data-cy={search.trim() ? 'workoutsNoResults' : 'workoutsEmpty'}>
                {search.trim() ? `No workouts match “${search.trim()}”.` : 'No previous workouts yet'}
              </Text>
            }
          />
        </Box>
      </Stack>

      <WorkoutPreviewModal
        opened={previewOpened}
        onClose={handleClosePreview}
        previewData={previewData}
        isLoading={workoutLoading}
        onEditBeforeAssign={handleEditBeforeAssign}
        onAssign={handleAssign}
        defaultVisibleToAthletes={defaultVisibleToAthletes}
        assignLoading={assignMutation.isLoading}
      />
    </>
  );
};

export default PanelWorkout;
